import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CacheWarmingResult as CacheWarmingResultModel } from "../services/cache-types";
import { formatDuration } from "./cache-utils";

interface CacheWarmingResultProps {
  result: CacheWarmingResultModel;
  onDismiss: () => void;
}

const CacheWarmingResult = ({ result, onDismiss }: CacheWarmingResultProps) => (
  <Box sx={{ mb: 3 }} data-testid="cache-warming-result">
    <Alert
      severity={result.success ? "success" : "error"}
      action={
        <IconButton
          aria-label="dismiss warming result"
          size="small"
          onClick={onDismiss}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
      sx={{ mb: result.cachedItems.length > 0 ? 1.5 : 0 }}
    >
      <AlertTitle>
        {result.success ? "Cache warmed" : "Cache warming failed"}
      </AlertTitle>
      {result.totalItemsWarmed} items warmed across {result.successfulCaches}{" "}
      cache{result.successfulCaches !== 1 ? "s" : ""} in{" "}
      {formatDuration(result.duration)}
      {result.failedCaches > 0 && ` — ${result.failedCaches} failed`}
      {result.errorMessage && ` — ${result.errorMessage}`}
    </Alert>

    {result.cachedItems.length > 0 && (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cache</TableCell>
              <TableCell align="right">Items</TableCell>
              <TableCell align="right">Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.cachedItems.map((item) => (
              <TableRow key={item.cacheName}>
                <TableCell>{item.cacheName}</TableCell>
                <TableCell align="right">{item.itemCount}</TableCell>
                <TableCell align="right">
                  {formatDuration(item.duration)}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={item.success ? "OK" : "Failed"}
                    color={item.success ? "success" : "error"}
                  />
                </TableCell>
                <TableCell>{item.errorMessage ?? "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

export default CacheWarmingResult;
